import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { Subscription, catchError, firstValueFrom, map, of } from 'rxjs';
import { BRIDGE_WINDOW, EDITOR_BRIDGE_VERSION, EmbeddedAuthPayload, HostToEditorMessage } from '@infinitform/platform-core';
import { AuthService } from '../auth/auth.service';
import { SessionService } from '../session.service';
import { ThemeService } from '../theme/theme.service';
import { WebSocketService } from '../websocket/websocket.service';
import { EmbeddedBridgeService } from './embedded-bridge.service';

/** Longest edge, in pixels, of the picture the cabinet shows for a paused or unloaded editor. */
export const SNAPSHOT_MAX_EDGE = 480;

/** Carries out what the cabinet asks of an embedded editor. */
@Injectable({ providedIn: 'root' })
export class EmbeddedEditorController {
  private readonly bridge = inject(EmbeddedBridgeService);
  private readonly auth = inject(AuthService);
  private readonly session = inject(SessionService);
  private readonly theme = inject(ThemeService);
  private readonly webSocket = inject(WebSocketService);
  private readonly document = inject(DOCUMENT);
  private readonly win = inject(BRIDGE_WINDOW);
  private subscription: Subscription | null = null;
  /** Set once hibernation starts; the GPU is released and the frame is about to be removed. */
  private hibernated = false;
  /** Set when the boot gave up waiting for a token; the next token reloads the frame. */
  private initialAuthTimedOut = false;
  private reloading = false;

  /** Starts listening to the cabinet and tells it this editor is ready. */
  start(): void {
    if (!this.bridge.isEmbedded || this.subscription) return;
    this.subscription = this.bridge.messages$.subscribe((message) => void this.handle(message));
    this.document.addEventListener('visibilitychange', this.onVisibilityChange);
    // The cabinet frames project URLs only, so the editor starts out loading one.
    this.bridge.setLoading(true);
    this.bridge.send({ type: 'ready', payload: { protocol: EDITOR_BRIDGE_VERSION } });
  }

  stop(): void {
    this.subscription?.unsubscribe();
    this.subscription = null;
    this.document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  /** True once the cabinet has handed over a token, false after `timeoutMs` without one. */
  waitForInitialAuth(timeoutMs: number): Promise<boolean> {
    return firstValueFrom(
      this.bridge.nextAuth(timeoutMs).pipe(
        map(() => true),
        catchError(() => {
          this.initialAuthTimedOut = true;
          return of(false);
        })
      )
    );
  }

  private async handle(message: HostToEditorMessage): Promise<void> {
    switch (message.type) {
      case 'auth':
        this.applyAuth(message.payload);
        break;
      case 'pause':
        this.bridge.hostPaused.set(true);
        this.session.engine?.pause();
        break;
      case 'resume':
        this.bridge.hostPaused.set(false);
        if (!this.hibernated && !this.document.hidden) this.session.engine?.resume();
        break;
      case 'snapshot-request':
        await this.sendSnapshot();
        break;
      case 'hibernate':
        await this.hibernate();
        break;
      case 'logout':
        this.auth.clearTokens();
        this.webSocket.disconnect();
        break;
      case 'theme':
        this.theme.applyHostTheme(message.payload.mode);
        break;
    }
  }

  private applyAuth(payload: EmbeddedAuthPayload): void {
    if (this.initialAuthTimedOut) {
      // The editor booted signed out; reloading boots it again with the cabinet's token.
      this.initialAuthTimedOut = false;
      this.reloading = true;
      this.win.location.reload();
      return;
    }
    if (this.reloading) return;
    const previous = this.auth.getAccessToken();
    this.auth.applyEmbeddedGrant(payload);
    // A hibernated editor keeps its socket closed.
    if (!this.hibernated && previous !== payload.token) this.webSocket.reconnectWithFreshToken();
  }

  private async sendSnapshot(): Promise<void> {
    try {
      const blob = await this.session.engine?.captureSnapshot(SNAPSHOT_MAX_EDGE);
      if (blob) this.bridge.send({ type: 'snapshot', payload: { blob } });
    } catch {
      // The cabinet keeps showing its previous picture.
    }
  }

  /** Saves what is pending and frees the GPU before the cabinet removes the frame. */
  private async hibernate(): Promise<void> {
    if (this.hibernated) return;
    this.hibernated = true;
    const engine = this.session.engine;
    // The auto-save timer must not run once the 3D view is released.
    this.session.stopAutoSave();
    await this.sendSnapshot();
    if (this.session.hasPendingAutoSave()) {
      try {
        await this.session.saveProject();
      } catch {
        // saveProject reports its own failures.
      }
    }
    engine?.releaseGpu();
    this.webSocket.disconnect();
    this.bridge.send({ type: 'hibernate-ack', payload: null });
  }

  private readonly onVisibilityChange = (): void => {
    const engine = this.session.engine;
    if (!engine || this.hibernated) return;
    if (this.document.hidden) {
      engine.pause();
    } else if (!this.bridge.hostPaused()) {
      engine.resume();
    }
  };
}
